/**
 * The only place in the web app that talks HTTP. Components use the stores,
 * stores use this (CLAUDE.md conventions: components never call fetch).
 *
 * Types come from `schema.d.ts`, generated from the API's OpenAPI document by
 * `pnpm --filter @refuelled/web generate:api`.
 */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const BASE = import.meta.env.VITE_API_BASE ?? '/api'

let accessToken: string | null = null
let onUnauthorized: (() => Promise<boolean>) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

/** Called once when a request 401s, to give the auth store a chance to refresh. */
export function setUnauthorizedHandler(handler: (() => Promise<boolean>) | null): void {
  onUnauthorized = handler
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  /** Internal: this is the retry after a successful refresh, so it gets no second go. */
  retrying?: boolean
  /**
   * Internal: this request *is* the refresh call.
   *
   * Without it, a 401 from `/auth/refresh` calls the unauthorized handler,
   * which is the thing that posts to `/auth/refresh`, which 401s again. That
   * loop is what put a white screen on the phone: it never threw, so the
   * token was never cleared and the session restore never settled. Worse, the
   * server rotates refresh tokens and treats a replayed one as theft, so every
   * turn of the loop revoked every session that person had anywhere.
   */
  isRefresh?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  if (response.status === 401 && !options.retrying && !options.isRefresh && onUnauthorized) {
    const refreshed = await onUnauthorized()
    if (refreshed) return request<T>(path, { ...options, retrying: true })
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload = text ? (JSON.parse(text) as unknown) : null

  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'internal',
      body?.error?.message ?? `Request failed with ${response.status}`,
      body?.error?.details,
    )
  }

  return payload as T
}

/**
 * Fetch bytes rather than JSON.
 *
 * Images are served through the API so the tenant check applies (ADR-013),
 * which means they need the Authorization header and cannot simply be an
 * `img` `src`. Returns null when there is nothing there.
 */
export async function requestBlob(
  path: string,
  query?: RequestOptions['query'],
  retrying = false,
): Promise<Blob | null> {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }

  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  const response = await fetch(url, { headers })

  if (response.status === 401 && !retrying && onUnauthorized) {
    if (await onUnauthorized()) return requestBlob(path, query, true)
  }
  if (response.status === 404) return null
  if (!response.ok) {
    throw new ApiError(response.status, 'internal', `Request failed with ${response.status}`)
  }
  return response.blob()
}

/** Send one file as multipart. The browser sets the boundary, so we must not. */
export async function upload<T>(
  path: string,
  file: Blob,
  filename = 'upload.jpg',
  retrying = false,
): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  const form = new FormData()
  form.append('file', file, filename)

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  const response = await fetch(url, { method: 'POST', headers, body: form })

  if (response.status === 401 && !retrying && onUnauthorized) {
    if (await onUnauthorized()) return upload<T>(path, file, filename, true)
  }

  const text = await response.text()
  const payload = text ? (JSON.parse(text) as unknown) : null
  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'internal',
      body?.error?.message ?? `Upload failed with ${response.status}`,
      body?.error?.details,
    )
  }
  return payload as T
}

/**
 * Where socket.io should connect.
 *
 * In a browser the API is same-origin and the default is right. Under
 * Capacitor the page's origin is `https://localhost` — the app's own bundle
 * inside the APK — so a socket left to the default asks the WebView's asset
 * loader for a websocket and retries forever. `VITE_API_BASE` is absolute in
 * a device build, and its origin is the answer.
 */
export function socketOrigin(): string | undefined {
  const base = import.meta.env.VITE_API_BASE
  if (!base || !/^https?:\/\//i.test(base)) return undefined
  return new URL(base).origin
}

/** For the one upload that cannot go through `request`: multipart form data. */
export function authorizationHeader(): string {
  return accessToken ? `Bearer ${accessToken}` : ''
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown, options?: Pick<RequestOptions, 'isRefresh'>) =>
    request<T>(path, { method: 'POST', body, ...options }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
